<template>
    <div class="media-gallery">
        <div
            id="blueimp-gallery"
            class="blueimp-gallery blueimp-gallery-controls"
            aria-label="image gallery"
            aria-modal="true"
            role="dialog"
        >
            <div class="slides" aria-live="polite"></div>
            <h3 class="title"></h3>
            <a
                class="prev"
                aria-controls="blueimp-gallery"
                aria-label="previous slide"
                aria-keyshortcuts="ArrowLeft"
            ></a>
            <a
                class="next"
                aria-controls="blueimp-gallery"
                aria-label="next slide"
                aria-keyshortcuts="ArrowRight"
            ></a>
            <a
                class="close"
                aria-controls="blueimp-gallery"
                aria-label="close"
                aria-keyshortcuts="Escape"
            ></a>
            <ol class="indicator"></ol>
        </div>
        
        <!-- Main image -->
        <div class="media-gallery__main" @click="openGallery(0)" :style="{'max-height': height/1.2 + 'px'}">
            <div class="media-gallery__overlay">
                <div v-if="showMediaManager && canUpload()">
                    <themed-button @click.stop="openMediaManager" variant="default" size="sm">
                        <inline-svg name="photo" classes="w-4 h-4 text-white fill-current"></inline-svg>
                        <span>Media Manager</span>
                    </themed-button>
                </div>
                <themed-badge variant="secondary">
                    {{ galleryMedia.length }} {{ galleryMedia.length | pluralize('Picture') }}
                </themed-badge>
            </div>
            <img :src="sortedMedia[0].thumbnail" :alt="sortedMedia[0].title" :class="objectCover ? 'object-cover' : 'object-contain'">
        </div>

        <!-- Thumbnail grid -->
        <div class="media-gallery__thumbnails">
            <div 
                v-for="(media, mediaIndex) in sortedMedia" 
                :key="mediaIndex"
                :class="[
                    'media-gallery__thumbnail',
                    { 'media-gallery__thumbnail--active': selectedImageIndex === mediaIndex },
                    { 'media-gallery__thumbnail--hidden': mediaIndex === 0 }
                ]"
                @click="selectImage(mediaIndex)"
            >
                <img :src="media.is_image ? media.href : media.poster" :alt="media.title">
            </div>
        </div>
        
        <!-- Hidden links for blueimp gallery -->
        <div id="links" style="display: none;">
            <a v-for="(media, mediaIndex) in sortedMedia" :key="mediaIndex" :href="media.href" :title="media.title" :data-poster="media.poster"></a>
        </div>

        <themed-modal 
            name="media-manager" 
            title="Media Manager"
            size="lg"
            :click-to-close="false"
            :show-footer="true">
            <media-manager
                ref="mediaManagerComponent"
                :documentable-id="documentableId"
                :documentable-type="documentableType"
                :media="galleryMedia"
                :show-upload-button="showUploadButton"
                @update:loading="isSaving = $event">
            </media-manager>
            
            <template #footer>
                <div class="flex items-center justify-between w-full">
                    <themed-button 
                        v-if="showUploadButton && canUpload()" 
                        @click="showMediaUpload" 
                        variant="default"
                        size="sm">
                        <inline-svg name="upload" classes="w-4 h-4 text-white fill-current"></inline-svg>
                        <span>Upload New Media</span>
                    </themed-button>
                    <div v-else></div>
                    <div class="flex items-center gap-2">
                        <themed-button variant="secondary" @click="closeMediaManager">
                            Close
                        </themed-button>
                        <themed-button variant="default" @click="saveMediaOrder" :disabled="isSaving">
                            <span v-if="isSaving">
                                <inline-svg name="loading-button"></inline-svg>                                
                            </span>
                            <span v-if="!isSaving">Save</span>
                        </themed-button>
                    </div>
                </div>
            </template>
        </themed-modal>

        <upload-document-native
            :documentable-id="documentableId"
            :documentable-type="documentableType"
            :allowed-categories="['Photo', 'Extras']"
            label="Upload Media"
            @documents-uploaded="handleDocumentsUploaded">
        </upload-document-native>
    </div>
</template>
 
<script>
    import 'blueimp-gallery/js/blueimp-helper.js';
    import 'blueimp-gallery/js/blueimp-gallery.js';
    import 'blueimp-gallery/js/blueimp-gallery-fullscreen.js';
    import 'blueimp-gallery/js/blueimp-gallery-video.js';
    import MediaManagerV1 from './MediaManager.vue';
    import ThemedButton from './ThemedButton.vue';
    import ThemedBadge from './ThemedBadge.vue';
    import ThemedModal from './ThemedModal.vue';
    import UploadDocumentNative from './documents/UploadDocumentNative.vue';

    export default {
        name: 'MediaGalleryV1',
        
        components: {
            'media-manager': MediaManagerV1,
            'themed-button': ThemedButton,
            'themed-badge': ThemedBadge,
            'themed-modal': ThemedModal,
            'upload-document-native': UploadDocumentNative
        },
        
        props: {
            documentableId: {
                type: Number,
                required: true
            },

            documentableType: {
                type: String,
                required: true
            },

            media: {
                type: [Array, Object],
                required: true
            },

            height: {
                type: Number,
                required: true
            },

            objectCover: {
                type: Boolean,
                default: true
            },

            showMediaManager: {
                type: Boolean,
                default: true
            },

            showUploadButton: {
                type: Boolean,
                default: true
            },
        },

        mounted() {
            const Gallery = window.blueimp && window.blueimp.Gallery;
            document.getElementById('links').onclick = (event) => {
                event = event || window.event
                var target = event.target || event.srcElement
                var link = target.src ? target.parentNode : target
                var options = { index: link, event: event }
                if (Gallery) Gallery(this.sortedMedia, options)
            }

            this.$eventHub.$on('add-to-gallery', (documents) => {
                this.finalizeUpload(documents);
            });

            this.$eventHub.$on('remove-from-gallery', (url) => {
                this.removeMediaFromGallery(url);
            });
        },

        data() {
            return {
                index: null,
                files: this.media,
                selectedImageIndex: 0,
                isSaving: false,
                options: {
                    closeOnSlideClick: true
                }
            };
        },

        computed: {
            galleryMedia() {
                return this.files;
            },

            sortedMedia: {
              get() {
                return _.map(_.sortBy(this.galleryMedia, 'sort_order'), (media) => {
                    return {
                        is_image: media.is_image,
                        is_video: media.is_video,
                        title: media.name,
                        type: media.is_image ? 'image/' + media.type : 'video/' + media.type,
                        href: media.view_url,
                        thumbnail: media.is_image ? media.view_url : '/img/video.png',
                        poster: media.is_image ? media.view_url : '/img/video.png'
                    }
                });
              },
              
              set(newValues) {
                this.galleryMedia = newValues;
              }
            }
        },

        methods: {
            canUpload() {
                // Allow if user can edit this specific site or has global create permission
                return this.$gate.can('update','site', this.documentableId) || this.$gate.can('create','site');
            },
            
            showDocumentUpload() {
                this.$modal.show('media-upload');
            },

            openMediaManager() {
                this.$modal.show('media-manager');
            },

            showMediaUpload() {
                this.$modal.show('document-upload');
            },

            closeMediaManager() {
                this.$modal.hide('media-manager');
            },

            saveMediaOrder() {
                if (this.$refs.mediaManagerComponent) {
                    this.$refs.mediaManagerComponent.saveSortOrders(true);
                }
            },

            handleDocumentsUploaded(documents) {
                // Documents are already handled by the event hub in UploadDocumentNative
                // This is just for any additional logic if needed
            },

            selectImage(index) {
                this.selectedImageIndex = index;
                this.openGallery(index);
            },

            openGallery(index) {
                const links = document.querySelectorAll('#links a');
                if (links[index]) {
                    links[index].click();
                }
            },

            finalizeUpload(documents) {
                // add new media to gallery
                for (const doc of Object.values(documents)) {
                    if (doc.is_image || doc.is_video) {
                        this.galleryMedia.push(doc);
                    }
                }

                // remove default sample image if it exists
                this.removeMediaFromGallery('/img/temp/building-sample.jpg');
                
                // close modal, filter if required
                this.$modal.hide('media-upload');
                this.$emit('filterRequest');
            },

            removeMediaFromGallery(url) {
                this.files = _.remove(this.files, (item) => {
                    return item.view_url != url;
                });

                if (this.galleryMedia.length == 0) {
                    this.files.push({
                        is_image: true,
                        is_video: false,
                        title: 'Sample Building Photo',
                        type: 'jpg',
                        href: '/img/temp/building-sample.jpg',
                        view_url: '/img/temp/building-sample.jpg',
                        thumbnail: '/img/temp/building-sample.jpg',
                        poster: '/img/temp/building-sample.jpg'
                    });
                }
            }
        }
    }
</script>
